import { useState, FormEvent } from 'react';

export default function RSVPForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    plusOne: 'no',
    plusOneName: '',
    partySize: '1',
    songRequest: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('plusOne', formData.plusOne);
      if (formData.plusOne === 'yes') {
        formBody.append('plusOneName', formData.plusOneName);
      }
      formBody.append('partySize', formData.partySize);
      formBody.append('songRequest', formData.songRequest);

      const response = await fetch('https://readdy.ai/api/form/d57gmgnahj0hkf71hus0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          plusOne: 'no',
          plusOneName: '',
          partySize: '1',
          songRequest: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="rsvp" className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <div className="w-16 h-px bg-gray-800 mx-auto mb-6"></div>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-gray-900 mb-4">RSVP</h2>
          <p className="text-gray-600 text-sm sm:text-base font-light tracking-wide">
            We would be honored by your presence
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-white p-6 sm:p-8 md:p-12 shadow-sm">
          <form id="wedding-rsvp-form" data-readdy-form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="fullName" className="block text-sm font-light tracking-wide text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-500 transition-colors text-sm"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-light tracking-wide text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-500 transition-colors text-sm"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="phone" className="block text-sm font-light tracking-wide text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-500 transition-colors text-sm"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-light tracking-wide text-gray-700 mb-3">
                Will you be bringing a plus one? *
              </label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="plusOne"
                    value="yes"
                    checked={formData.plusOne === 'yes'}
                    onChange={(e) => setFormData({ ...formData, plusOne: e.target.value })}
                    className="mr-2 cursor-pointer"
                  />
                  <span className="text-sm font-light text-gray-700">Yes</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="plusOne"
                    value="no"
                    checked={formData.plusOne === 'no'}
                    onChange={(e) => setFormData({ ...formData, plusOne: e.target.value })}
                    className="mr-2 cursor-pointer"
                  />
                  <span className="text-sm font-light text-gray-700">No</span>
                </label>
              </div>
            </div>

            {formData.plusOne === 'yes' && (
              <div className="mb-6">
                <label htmlFor="plusOneName" className="block text-sm font-light tracking-wide text-gray-700 mb-2">
                  Plus One Name *
                </label>
                <input
                  type="text"
                  id="plusOneName"
                  name="plusOneName"
                  value={formData.plusOneName}
                  onChange={(e) => setFormData({ ...formData, plusOneName: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-500 transition-colors text-sm"
                />
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="partySize" className="block text-sm font-light tracking-wide text-gray-700 mb-2">
                How many total in your party will be attending? *
              </label>
              <select
                id="partySize"
                name="partySize"
                value={formData.partySize}
                onChange={(e) => setFormData({ ...formData, partySize: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-500 transition-colors text-sm cursor-pointer"
              >
                <option value="1">1 person</option>
                <option value="2">2 people</option>
                <option value="3">3 people</option>
                <option value="4">4 people</option>
                <option value="5">5 people</option>
                <option value="6">6 people</option>
                <option value="7">7 people</option>
                <option value="8">8 people</option>
              </select>
            </div>

            <div className="mb-8">
              <label htmlFor="songRequest" className="block text-sm font-light tracking-wide text-gray-700 mb-2">
                You promise to dance if we play what song? *
              </label>
              <textarea
                id="songRequest"
                name="songRequest"
                value={formData.songRequest}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, songRequest: e.target.value });
                  }
                }}
                maxLength={500}
                required
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-500 transition-colors text-sm resize-none"
                placeholder="Tell us your favorite song to dance to..."
              />
              <p className="text-xs text-gray-500 mt-1 font-light">
                {formData.songRequest.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-900 text-white py-3 sm:py-4 text-xs sm:text-sm tracking-widest font-light hover:bg-gray-800 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'SUBMITTING...' : 'SUBMIT RSVP'}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 text-center">
                <p className="text-sm text-green-800 font-light">
                  Thank you for your RSVP! We look forward to celebrating with you.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 text-center">
                <p className="text-sm text-red-800 font-light">
                  Something went wrong. Please try again.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
